// src/pages/CoFounderPage.js
import React, { useState } from 'react';
import { Brain, Settings, PanelRightOpen, PanelRightClose, Zap } from 'lucide-react';
import CoFounderMessageList from '../components/cofounder/CoFounderMessageList';
import CoFounderInput from '../components/cofounder/CoFounderInput';
import ConversationSidebar from '../components/cofounder/ConversationSidebar';
import InsightsList from '../components/cofounder/InsightsList';
import ActionsList from '../components/cofounder/ActionsList';
import QuickActions from '../components/cofounder/QuickActions';
import CoFounderSettings from '../components/cofounder/CoFounderSettings';
import CoFounderOnboarding from '../components/cofounder/CoFounderOnboarding';
import { useCoFounder } from '../context/CoFounderContext';

const CoFounderPage = () => {
  const {
    sendMessage, isLoading, messages, hasOnboarded,
    unreadInsightCount, unreadActionCount, setIsOpen
  } = useCoFounder();

  const [showSidebar, setShowSidebar] = useState(true);
  const [showInsights, setShowInsights] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Close the floating widget when on the full page
  React.useEffect(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  if (!hasOnboarded) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 min-h-[80vh] flex items-center justify-center">
        <CoFounderOnboarding />
      </div>
    );
  }

  const handleInsightAsk = (insight) => {
    setShowInsights(false);
    sendMessage(`Tell me more about this: ${insight.title}. ${insight.description}`);
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 flex h-[calc(100vh-120px)] overflow-hidden">
      {/* Conversation Sidebar */}
      {showSidebar && (
        <ConversationSidebar className="w-64 flex-shrink-0 hidden lg:flex" />
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 hidden lg:flex"
            >
              {showSidebar ? <PanelRightClose size={18} /> : <PanelRightOpen size={18} />}
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#190192] to-[#4f46e5] flex items-center justify-center">
                <Brain size={16} className="text-white" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-800">coFounder</h2>
                <p className="text-xs text-gray-500">Your AI business partner</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => { setShowActions(!showActions); if (!showActions) setShowInsights(false); }}
              className={`p-2 rounded-lg transition-colors relative flex items-center gap-1 ${
                showActions ? 'bg-purple-100 text-[#190192]' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Zap size={14} />
              <span className="text-xs font-medium">Actions</span>
              {unreadActionCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                  {unreadActionCount}
                </span>
              )}
            </button>
            <button
              onClick={() => { setShowInsights(!showInsights); if (!showInsights) setShowActions(false); }}
              className={`p-2 rounded-lg transition-colors relative ${
                showInsights ? 'bg-purple-100 text-[#190192]' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="text-xs font-medium">Insights</span>
              {unreadInsightCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                  {unreadInsightCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            >
              <Settings size={16} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <CoFounderMessageList />

        {/* Quick Actions (shown when no messages) */}
        {messages.length === 0 && <QuickActions onSelect={sendMessage} />}

        {/* Input */}
        <CoFounderInput onSend={sendMessage} isLoading={isLoading} />
      </div>

      {/* Actions Panel */}
      {showActions && (
        <div className="w-72 border-l border-gray-200 flex-shrink-0 flex flex-col hidden lg:flex">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-800">Actions</h3>
            <p className="text-xs text-gray-500">Business operations performed by coFounder</p>
          </div>
          <div className="flex-1 overflow-y-auto p-3">
            <ActionsList />
          </div>
        </div>
      )}

      {/* Insights Panel */}
      {showInsights && (
        <div className="w-72 border-l border-gray-200 flex-shrink-0 flex flex-col hidden lg:flex">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-800">Insights</h3>
            <p className="text-xs text-gray-500">Proactive alerts about your business</p>
          </div>
          <div className="flex-1 overflow-y-auto p-3">
            <InsightsList onAskAbout={handleInsightAsk} />
          </div>
        </div>
      )}

      {/* Settings Modal */}
      <CoFounderSettings isOpen={showSettings} onClose={() => setShowSettings(false)} />
    </div>
  );
};

export default CoFounderPage;
